import { create } from 'zustand';
import {
  fetchMyMessages,
  fetchUnreadMessageCount,
  markAllMessagesRead,
  markMessageRead,
} from '@/service/api/notification';

interface NotificationStore {
  unreadCount: number;
  messages: Api.Notification.MessageResponse[];
  total: number;
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  page: number;
  pageSize: number;

  // Actions
  fetchUnreadCount: () => Promise<number>;
  fetchMessages: (params?: Api.Notification.GetMessagesParams) => Promise<void>;
  fetchMoreMessages: (params?: Api.Notification.GetMessagesParams) => Promise<void>;
  markAsRead: (id: string | number) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  startPolling: (intervalMs?: number) => void;
  stopPolling: () => void;
}

let pollTimer: ReturnType<typeof setInterval> | null = null;

export const useNotificationStore = create<NotificationStore>((set, get) => ({
  unreadCount: 0,
  messages: [],
  total: 0,
  loading: false,
  loadingMore: false,
  hasMore: true,
  page: 1,
  pageSize: 10,

  fetchUnreadCount: async () => {
    try {
      const res = await fetchUnreadMessageCount();
      const count = res?.count ?? 0;
      set({ unreadCount: count });
      return count;
    } catch (err) {
      console.error('获取未读消息数失败', err);
      return get().unreadCount;
    }
  },

  fetchMessages: async (params) => {
    set({ loading: true });
    try {
      const page = params?.page ?? 1;
      const pageSize = params?.page_size ?? get().pageSize;
      const res = await fetchMyMessages({
        page,
        page_size: pageSize,
        is_read: params?.is_read,
        message_type: params?.message_type,
      });

      const items = res?.items || [];
      const total = res?.total || 0;

      set({
        messages: items,
        total,
        page,
        pageSize,
        hasMore: items.length < total,
      });

      // 同步获取未读数
      void get().fetchUnreadCount();
    } catch (err) {
      console.error('获取消息列表失败', err);
      set({ messages: [], total: 0, hasMore: false });
    } finally {
      set({ loading: false });
    }
  },

  fetchMoreMessages: async (params) => {
    const { page, total, messages, loadingMore, loading, hasMore } = get();
    if (loadingMore || loading || !hasMore) return;

    set({ loadingMore: true });
    try {
      const nextPage = page + 1;
      const pageSize = params?.page_size ?? get().pageSize;
      const res = await fetchMyMessages({
        page: nextPage,
        page_size: pageSize,
        is_read: params?.is_read,
        message_type: params?.message_type,
      });

      const newItems = res?.items || [];
      const newTotal = res?.total || total;

      const existingIds = new Set(messages.map((m) => m.id));
      const filteredNew = newItems.filter((m) => !existingIds.has(m.id));
      const combined = [...messages, ...filteredNew];

      set({
        messages: combined,
        total: newTotal,
        page: nextPage,
        hasMore: combined.length < newTotal && newItems.length > 0,
      });
    } catch (err) {
      console.error('加载更多消息失败', err);
    } finally {
      set({ loadingMore: false });
    }
  },

  markAsRead: async (id) => {
    try {
      await markMessageRead(id);
      // 本地状态乐观更新
      set((state) => {
        const nextMessages = state.messages.map((m) =>
          m.id === String(id) ? { ...m, is_read: true } : m
        );
        const nextUnread = Math.max(0, state.unreadCount - 1);
        return { messages: nextMessages, unreadCount: nextUnread };
      });
      // 触发后台精确刷新
      void get().fetchUnreadCount();
    } catch (err) {
      console.error('标记消息已读失败', err);
    }
  },

  markAllAsRead: async () => {
    try {
      await markAllMessagesRead();
      // 本地更新
      set((state) => ({
        unreadCount: 0,
        messages: state.messages.map((m) => ({ ...m, is_read: true })),
      }));
      void get().fetchUnreadCount();
    } catch (err) {
      console.error('全部标记已读失败', err);
    }
  },

  startPolling: (intervalMs = 30000) => {
    if (pollTimer) return;
    // 立即执行一次获取未读数
    void get().fetchUnreadCount();
    pollTimer = setInterval(() => {
      void get().fetchUnreadCount();
    }, intervalMs);
  },

  stopPolling: () => {
    if (pollTimer) {
      clearInterval(pollTimer);
      pollTimer = null;
    }
  },
}));
